// src/components/featureAdjudication/CellRow.jsx
//
// One row of the adjudication cell list. Forked from wellfinder/QueueRow.jsx and
// keeps its signature — { row, selected, isMine, onSelect } — so a reviewer
// moving between the two surfaces is reading the same shape: a fixed left badge,
// one truncating identity column, a fixed right column for state.
//
// What is NOT forked is the meaning of the badge. QueueRow's is a tier, a
// qualitative label; this one is a count, because a cell has no tier and the
// only thing that ranks one cell above another is how much is in it.
//
// Deliberately dumb, for the same reason QueueRow is: it takes a payload and
// three booleans and calls onSelect. There is no store behind this screen.

/**
 * "storage_tank_boundary" -> "storage tank".
 *
 * DERIVED, NOT A LOOKUP TABLE. Every class_key in the queue ends in `_boundary`
 * (confirmed: 0 of 229 rows do not), so the suffix is shared by every value and
 * distinguishes nothing — it is pure column width on a scanning surface. A map
 * would also have to be maintained: today's four keys are storage_tank,
 * separator, well_head and valve, and a fifth arriving from a new detector would
 * render as a raw key or, worse, as nothing. This renders it sensibly unseen.
 */
export function classLabel(key) {
  return String(key ?? "").replace(/_boundary$/, "").replace(/_/g, " ");
}

/** "24 min" — how long the lock has left. Nobody reads an expiry timestamp. */
function lockRemaining(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const min = Math.round(ms / 60000);
  return min < 1 ? "under a minute" : `${min} min`;
}

export default function CellRow({ row, selected, isMine, done = false, onSelect }) {
  // Held means ANOTHER reviewer on a LIVE lock. Your own lock is not contention
  // -- it is your working set, and rendering it amber would make the cell you
  // are in look like a problem. The server has already dropped expired locks
  // (`l.expires_at > now()` in the join), so the client never reimplements the
  // TTL; lockRemaining only formats what survived it.
  //
  // This mirrors QueueRow's `occupied` exactly, including the reason.
  const held = Boolean(row.locked_by) && !isMine;
  const mine = Boolean(row.locked_by) && isMine;
  const left = lockRemaining(row.lock_expires_at);

  const classes = Array.isArray(row.classes) ? row.classes : [];

  return (
    <button
      type="button"
      onClick={() => onSelect?.(row)}
      aria-current={selected ? "true" : undefined}
      data-testid={`cell-row-${row.cell_x}-${row.cell_y}`}
      className={[
        "w-full text-left grid items-start gap-x-3 px-2.5 py-2",
        // Same three-track shape as QueueRow, same reason: fixed bases only for
        // content of known width, and the one unbounded column is minmax(0,1fr)
        // so it truncates instead of refusing to shrink below its longest word.
        "grid-cols-[2.5rem_minmax(0,1fr)_6.5rem]",
        "border-b border-bg-border text-xs",
        selected ? "bg-accent-action/15" : "hover:bg-white/[0.03]",
        // A CELL FINISHED THIS SESSION STAYS IN THE RAIL, DIMMED.
        //
        // The server drops it from /cells the moment it reaches zero -- that is
        // how -7005,2259 vanished. Under one page that would take away the row
        // the reviewer is standing on: the detail pane empties and the selection
        // jumps somewhere they did not choose. So the rail keeps it until they
        // move, and it disappears on the next natural refetch.
        done ? "opacity-45" : "",
      ].join(" ")}
    >
      {/* Outstanding. The ordering key, so it leads -- the server returns the
          list count-descending and the eye should be able to follow that down
          the left edge without reading anything else. */}
      <span className="justify-self-start rounded bg-white/5 px-1.5 py-0.5 text-[11px]
                       font-semibold tabular-nums text-fg-primary ring-1 ring-white/10">
        {row.outstanding}
      </span>

      <span className="min-w-0">
        {/* Monospace, because these are coordinates read digit by digit: the
            cells differ in their interior digits (-7145 vs -7148) and a
            proportional font makes that comparison harder than it needs to be. */}
        <span className="block truncate font-mono tabular-nums text-fg-primary">
          {row.cell_x}, {row.cell_y}
        </span>
        <span className="block truncate text-[11px] text-fg-secondary/70">
          {classes.length ? classes.map(classLabel).join(" · ") : "—"}
        </span>
      </span>

      {/* Lock, or completion. Empty for the common case: 0 of 15 cells are
          locked, and a column that says "unlocked" fifteen times trains the eye
          to skip the one row where it matters. */}
      <span className="justify-self-end text-right">
        {done && <span className="block text-emerald-400/80">done</span>}
        {held && (
          <>
            <span className="block text-amber-300/90">in use</span>
            {left && <span className="block text-[11px] text-fg-secondary/60">{left} left</span>}
          </>
        )}
        {mine && (
          <>
            <span className="block text-fg-secondary">yours</span>
            {left && <span className="block text-[11px] text-fg-secondary/60">{left} left</span>}
          </>
        )}
      </span>
    </button>
  );
}
